import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Linkedin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const Footer = () => {
  const { language } = useLanguage();
  
  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company info */}
          <div>
            <div className="text-2xl font-bold mb-4">ServiceMind24</div>
            <p className="text-primary-foreground/80 mb-6">
              {language === "en"
                ? "Your reliable partner for professional moving, cleaning, repair and handyman services in Helsinki."
                : "Luotettava kumppanisi ammattimaisissa muutto-, siivous-, korjaus- ja käsityöpalveluissa Helsingissä."
              }
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <Phone className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                <span>093 158 8300</span>
              </div>
              <div className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                <span>24@servicehelsinki.com</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                <span>Kaarenjalka 4D<br/>00940 Helsinki<br/>{language === "en" ? "Finland" : "Suomi"}</span>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                <span>{language === "en" ? "Available 24/7" : "Saatavilla 24/7"}</span>
              </div>
              <div className="mt-3 text-xs text-primary-foreground/60">
                <div>{language === "en" ? "Founder: Vijay A" : "Perustaja: Vijay A"}</div>
                <div>Y-tunnus: 3533969-6</div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {language === "en" ? "Our Services" : "Palvelumme"}
            </h3>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li><a href="#services" className="hover:text-accent transition-smooth">
                {language === "en" ? "Moving & Transport" : "Muutto & Kuljetus"}
              </a></li>
              <li><a href="#services" className="hover:text-accent transition-smooth">
                {language === "en" ? "Professional Cleaning" : "Ammattisiivous"}
              </a></li>
              <li><a href="#services" className="hover:text-accent transition-smooth">
                {language === "en" ? "Repair & Handyman" : "Korjaus & Käsityö"}
              </a></li>
              <li><a href="#services" className="hover:text-accent transition-smooth">
                {language === "en" ? "Installation & Assembly" : "Asennus & Kokoaminen"}
              </a></li>
              <li><a href="#services" className="hover:text-accent transition-smooth">
                {language === "en" ? "Combo Packages" : "Yhdistelmäpaketit"}
              </a></li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {language === "en" ? "Quick Links" : "Pikalinkit"}
            </h3>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li><a href="/" className="hover:text-accent transition-smooth">
                {language === "en" ? "Home" : "Etusivu"}
              </a></li>
              <li><a href="/about" className="hover:text-accent transition-smooth">
                {language === "en" ? "About Us" : "Tietoa meistä"}
              </a></li>
              <li><a href="/#services" className="hover:text-accent transition-smooth">
                {language === "en" ? "Services" : "Palvelut"}
              </a></li>
              <li><a href="/#contact" className="hover:text-accent transition-smooth">
                {language === "en" ? "Contact" : "Yhteystiedot"}
              </a></li>
              <li><a href="/payment-policy" className="hover:text-accent transition-smooth">
                {language === "en" ? "Payment & Cancellation Policy" : "Maksu- ja peruutuskäytäntö"}
              </a></li>
              <li><a href="/members" className="hover:text-accent transition-smooth">
                {language === "en" ? "For Members" : "Jäsenille"}
              </a></li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {language === "en" ? "Stay Updated" : "Pysy ajan tasalla"}
            </h3>
            <p className="text-sm text-primary-foreground/80 mb-4">
              {language === "en"
                ? "Get tips, special offers and service updates to your email."
                : "Saa vinkkejä, erikoistarjouksia ja palvelupäivityksiä sähköpostiisi."
              }
            </p>
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input 
                  placeholder={language === "en" ? "Your email address" : "Sähköpostiosoitteesi"}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/60 text-sm"
                />
                <Button variant="minimal" size="sm" className="whitespace-nowrap">
                  {language === "en" ? "Subscribe" : "Tilaa"}
                </Button>
              </div>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/profile.php?id=61581152632250" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-smooth" aria-label="Facebook">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="#" className="hover:text-accent transition-smooth" aria-label="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="#" className="hover:text-accent transition-smooth" aria-label="LinkedIn">
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/20">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs">
            <div className="text-primary-foreground/60">
              © 2025 ServiceMind24. {language === "en" ? "All rights reserved." : "Kaikki oikeudet pidätetään."}
            </div>
            <div className="flex gap-4 text-primary-foreground/60">
              <a href="#" className="hover:text-accent transition-smooth">
                {language === "en" ? "Privacy Policy" : "Tietosuojakäytäntö"}
              </a>
              <a href="#" className="hover:text-accent transition-smooth">
                {language === "en" ? "Terms of Service" : "Käyttöehdot"}
              </a>
            </div>
          </div>
          <div className="text-center mt-4 text-xs text-primary-foreground/60">
            Website built by{" "}
            <a 
              href="https://www.instagram.com/openmedia_solutions/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-accent transition-smooth underline"
            >
              OpenMedia Solutions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;